import React, { useState } from 'react';
import { Search, Loader2, CheckCircle2, XCircle, Zap, PlusCircle, AlertTriangle } from 'lucide-react';
import ManualAffiliateDetails from './ManualAffiliateDetails';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const fmt = (n) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString());

/**
 * Entry point for the paid-ads workflow, whether the ad was spotted in Google's
 * sponsored results or in Meta's Ad Library:
 *
 *   paste a domain -> is it in affiliate_clean?
 *     yes -> Set paid traffic
 *     no  -> Add it now (runs the SEMrush pipeline) -> Set paid traffic
 *
 * Without this the same job means bouncing between /paid-traffic and
 * /add-affiliate and searching twice.
 */
const DomainCheckBar = ({ onSetPaidTraffic, onAdded }) => {
  const [domain, setDomain] = useState('');
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // True once the server reports SEMrush has nothing for this domain.
  const [needsDetails, setNeedsDetails] = useState(false);

  const check = async (e) => {
    e?.preventDefault();
    const value = domain.trim();
    if (!value) return;

    setChecking(true);
    setError('');
    setResult(null);
    setNeedsDetails(false);
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/lookup?domain=${encodeURIComponent(value)}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Lookup failed');
    } finally {
      setChecking(false);
    }
  };

  const addNow = async (extra = {}) => {
    if (!result?.domain) return;

    setAdding(true);
    setError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/add-affiliate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: result.domain, ...extra })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Server error: ${res.status}`);

      // SEMrush knows nothing about it — collect country and vertical, then retry.
      if (data.needsManualDetails) {
        setNeedsDetails(true);
        return;
      }

      if (data.success && data.affiliate) {
        setNeedsDetails(false);
        onAdded?.(data.affiliate, data.message);
        // Straight into the paid-traffic editor — that is the whole point of the flow.
        onSetPaidTraffic(data.affiliate);
        setResult({ ...result, found: true, data: data.affiliate, justAdded: true });
      } else {
        setError(data.message || 'The affiliate could not be added.');
      }
    } catch (err) {
      setError(err.message || 'Failed to add affiliate');
    } finally {
      setAdding(false);
    }
  };

  const row = result?.data;

  return (
    <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-900/40 dark:bg-blue-900/10">
      <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-slate-100">
        Found a sponsored ad? Check the domain
      </h2>
      <p className="mb-3 text-xs text-gray-500 dark:text-slate-400">
        Paste the domain from the Google ad. If it is already in the database you can set its paid traffic straight
        away; if not, add it first without leaving this page.
      </p>

      <form onSubmit={check} className="flex flex-wrap gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="bonusfinder.com"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>
        <button
          type="submit"
          disabled={checking || !domain.trim()}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Check
        </button>
      </form>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && !error && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          {result.found && row ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                  <span className="font-medium text-gray-900 dark:text-slate-100">{row.affiliate_url}</span>
                  <span className="text-xs text-gray-500 dark:text-slate-400">
                    {result.justAdded ? 'added just now' : 'is in the database'}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-slate-400">
                  <span>Organic {fmt(row.organic_traffic)}</span>
                  <span>Paid {fmt(row.adwords_traffic)}</span>
                  {row.vertical && <span>{row.vertical}</span>}
                  {result.duplicateCount > 1 && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {result.duplicateCount} duplicate rows — editing the newest
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onSetPaidTraffic(row)}
                className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
              >
                <Zap className="h-4 w-4" />
                Set paid traffic
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-slate-300">
                    <span className="font-medium">{result.domain}</span> is not in the database yet.
                  </span>
                </div>
                {!needsDetails && (
                  <button
                    type="button"
                    onClick={() => addNow()}
                    disabled={adding}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                    {adding ? 'Running SEMrush pipeline…' : 'Add it now'}
                  </button>
                )}
              </div>

              {needsDetails && (
                <ManualAffiliateDetails
                  domain={result.domain}
                  busy={adding}
                  compact
                  onConfirm={({ country, vertical }) => addNow({ country, vertical })}
                  onCancel={() => setNeedsDetails(false)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DomainCheckBar;
